import { useEffect, useState } from 'react';
import { BASE_SERVER_URL } from 'src/config';
import axios from 'src/utils/axios';

const DOMAIN_CHECK_DEBOUNCE_MS = 500;

/** `idle` means there is nothing to check — disabled, empty, or failing format validation. */
export type DomainStatus = 'idle' | 'checking' | 'available' | 'taken';

type UseDomainAvailabilityProps = {
  email: string;
  /** Fully qualified domain to check. Pass an empty string to skip the check. */
  domain: string;
  enabled: boolean;
};

/**
 * Checks tenant domain availability against the backend, debounced on the domain value.
 * Format validation lives in the form schema — this hook only answers "is it taken?".
 */
export const useDomainAvailability = ({
  email,
  domain,
  enabled
}: UseDomainAvailabilityProps) => {
  const [domainStatus, setDomainStatus] = useState<DomainStatus>('idle');

  useEffect(() => {
    if (!enabled || !domain) {
      setDomainStatus('idle');
      return;
    }

    // `checking` starts before the debounce rather than after it, so the window
    // between a keystroke and the request never reads as a settled answer.
    setDomainStatus('checking');

    let isActive = true;
    const timer = setTimeout(() => {
      const url = `${BASE_SERVER_URL}/api/v1/tenant/verify-domain?domain=${domain}&user_email=${email}`;
      void axios
        .get(url)
        .then(
          (resp: {
            data?: { ret_val?: { data?: { is_available?: boolean } } };
          }) => {
            if (isActive) {
              setDomainStatus(
                resp?.data?.ret_val?.data?.is_available === true
                  ? 'available'
                  : 'taken'
              );
            }
          }
        )
        .catch(() => {
          // A failed lookup is treated as unavailable so an unverified domain never ships.
          if (isActive) {
            setDomainStatus('taken');
          }
        });
    }, DOMAIN_CHECK_DEBOUNCE_MS);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [domain, email, enabled]);

  return { domainStatus };
};
